import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Upload, Loader2, X, Check } from 'lucide-react';
import { documentsApi } from '../lib/api';
import { formatFileSize } from '../lib/utils';
import { toast } from 'sonner';

const DOCUMENT_TYPES = [
  { id: 'care_notes', label: 'Care notes' },
  { id: 'medical', label: 'Medical records' },
  { id: 'education', label: 'Education records' },
  { id: 'id_proof', label: 'Proof of ID' },
  { id: 'address_proof', label: 'Proof of address' },
  { id: 'other', label: 'Other' },
];

// Keep in step with CONVERTIBLE in backend/app/services/document_conversion.py
// (plus PDF and images, which take the OCR path). Legacy .doc is deliberately
// absent: nothing server-side can read it, so we say so up front.
export const ACCEPTED = [
  'pdf', 'docx', 'pptx', 'xlsx', 'xls', 'csv', 'txt', 'md', 'rtf', 'html', 'eml', 'msg',
  'jpg', 'jpeg', 'png', 'gif',
];
const ACCEPTED_SUMMARY = 'PDF, Word, Excel, PowerPoint, email, text and images';
const REJECTED_ADVICE = {
  doc: 'Older .doc files cannot be read here. Open it in Word and save it as .docx first.',
  odt: 'Open it in LibreOffice and save it as .docx first.',
  ods: 'Open it in LibreOffice and save it as .xlsx first.',
  odp: 'Open it in LibreOffice and save it as .pptx first.',
  pages: 'Export it from Pages as .docx or PDF first.',
  numbers: 'Export it from Numbers as .xlsx first.',
  zip: 'Unzip it and upload the files inside instead — you can pick them all at once.',
};

// Two picks of the same file (name and size) are one staged file.
const fileKey = (f) => `${f.name}:${f.size}`;

/**
 * Upload Document dialog with drag-and-drop support. Several files can be
 * staged at once (a SAR response often arrives as a dozen PDFs); they
 * share the organisation and document type chosen here and are uploaded
 * one after another, each encrypted on its own. A file that fails stays
 * staged so it can be retried without picking the others again.
 *
 * Props:
 *  - open, onOpenChange — dialog visibility
 *  - organisations — list of orgs for the org dropdown
 *  - onUploaded — callback after each successful upload (parent reloads docs)
 *  - prefilledFiles — optional File objects pre-loaded (e.g. from a
 *    page-level drop zone) so the dialog opens with them already staged
 *    (`prefilledFile`, a single File, is still accepted)
 *  - defaultOrgId — optional org to pre-select (e.g. when launched from
 *    `/documents?org={id}`)
 */
export const UploadDocumentDialog = ({
  open,
  onOpenChange,
  organisations,
  onUploaded,
  prefilledFile,
  prefilledFiles,
  defaultOrgId,
}) => {
  const [orgId, setOrgId] = useState(defaultOrgId || '');
  const [docType, setDocType] = useState('');
  const [files, setFiles] = useState([]);
  // name+size → 'uploading' | 'done' | 'failed' while a batch is in flight
  const [progress, setProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  // Reset / preload state when the dialog opens
  useEffect(() => {
    if (open) {
      setOrgId(defaultOrgId || '');
      setDocType('');
      setProgress({});
      const staged = prefilledFiles?.length ? prefilledFiles : (prefilledFile ? [prefilledFile] : []);
      setFiles(staged.filter((f) => validateFile(f)));
    } else {
      // Allow exit transition then clear
      const t = setTimeout(() => {
        setFiles([]);
        setProgress({});
        setUploading(false);
      }, 200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefilledFile, prefilledFiles, defaultOrgId]);

  const validateFile = useCallback((f) => {
    if (!f) return false;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!ACCEPTED.includes(ext)) {
      toast.error(REJECTED_ADVICE[ext] || `We cannot accept .${ext} files (${f.name}). Use ${ACCEPTED_SUMMARY.toLowerCase()}.`);
      return false;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error(`${f.name} is over 50 MB.`);
      return false;
    }
    return true;
  }, []);

  // Stage every valid file picked or dropped, skipping ones already staged.
  const handleFiles = useCallback((fileList) => {
    const picked = Array.from(fileList || []).filter(validateFile);
    if (!picked.length) return;
    setFiles((prev) => {
      const have = new Set(prev.map(fileKey));
      return [...prev, ...picked.filter((f) => !have.has(fileKey(f)))];
    });
  }, [validateFile]);

  const removeFile = (f) => setFiles((prev) => prev.filter((x) => fileKey(x) !== fileKey(f)));

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    handleFiles(e.dataTransfer?.files);
  }, [handleFiles]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  }, [dragOver]);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  // One request per file, in order: each is encrypted server-side on its
  // own, and a failure part-way leaves the rest to carry on. Files that
  // uploaded are taken off the list; failed ones stay staged for a retry.
  const handleSubmit = async () => {
    if (!files.length) {
      toast.error('Choose a file first.');
      return;
    }
    if (!orgId) {
      toast.error('Choose an organisation first.');
      return;
    }
    if (!docType) {
      toast.error('Choose a document type first.');
      return;
    }

    setUploading(true);
    const failed = [];
    let done = 0;
    for (const f of files) {
      setProgress((p) => ({ ...p, [fileKey(f)]: 'uploading' }));
      try {
        await documentsApi.upload(f, orgId, docType);
        done += 1;
        setProgress((p) => ({ ...p, [fileKey(f)]: 'done' }));
        onUploaded?.();
      } catch (error) {
        failed.push(f);
        setProgress((p) => ({ ...p, [fileKey(f)]: 'failed' }));
      }
    }
    setUploading(false);
    if (!failed.length) {
      toast.success(done === 1 ? 'Document uploaded and encrypted.' : `${done} documents uploaded and encrypted.`);
      onOpenChange(false);
      return;
    }
    setFiles(failed);
    setProgress({});
    toast.error(
      failed.length === files.length
        ? (failed.length === 1 ? 'The upload did not go through. Try again.' : 'None of the uploads went through. Try again.')
        : `${done} uploaded; ${failed.length} did not go through. Try ${failed.length === 1 ? 'it' : 'them'} again.`
    );
  };

  const uploadingIndex = files.findIndex((f) => progress[fileKey(f)] === 'uploading');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="upload-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-left text-[22px] font-semibold tracking-[-0.02em]">Upload a document</DialogTitle>
          <DialogDescription className="text-left text-ink-55">
            Files are encrypted with your key before they are stored. We cannot read them. Several files at once
            all go under the organisation and type you choose here.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Org + type selects */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[15px] font-semibold">Which organisation sent it?</Label>
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger data-testid="upload-org-select">
                  <SelectValue placeholder="Choose an organisation" />
                </SelectTrigger>
                <SelectContent>
                  {organisations.length === 0 ? (
                    <div className="text-sm text-ink-55 p-2">No organisations yet</div>
                  ) : (
                    organisations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[15px] font-semibold">What kind of document is it?</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="upload-doctype-select">
                  <SelectValue placeholder="Choose a type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Drop zone — stays available with files staged, so more can be added */}
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => !uploading && inputRef.current?.click()}
            className={`relative rounded-lg border-2 border-dashed transition-colors duration-state ${
              uploading ? 'cursor-default opacity-60' : 'cursor-pointer'
            } ${
              dragOver
                ? 'border-brand bg-brand-wash/40'
                : 'border-rule bg-ground hover:border-brand/50'
            } ${files.length ? 'px-4 py-4' : 'px-6 py-10'} text-center`}
            data-testid="upload-dropzone"
          >
            {!files.length && (
              <Upload className={`w-8 h-8 mx-auto mb-3 stroke-[1.5] ${dragOver ? 'text-brand' : 'text-ink-55'}`} aria-hidden="true" />
            )}
            <p className={`font-medium text-ink ${files.length ? 'text-sm' : 'text-[15px] mb-1'}`}>
              {dragOver
                ? 'Drop to add'
                : files.length
                  ? 'Drag more files here, or click to add them'
                  : 'Drag files here, or click to choose them'}
            </p>
            {!files.length && (
              <p className="text-sm text-ink-55">
                {ACCEPTED_SUMMARY} · up to 50 MB each · several at once is fine
              </p>
            )}
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
              accept={ACCEPTED.map(e => `.${e}`).join(',')}
            />
          </div>

          {files.length > 0 && (
            <ul className="rounded-lg bg-ground divide-y divide-rule max-h-56 overflow-y-auto" data-testid="upload-file-list">
              {files.map((f) => {
                const state = progress[fileKey(f)];
                return (
                  <li key={fileKey(f)} className="p-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[15px] truncate text-ink">{f.name}</p>
                      <p className="text-sm text-ink-55">
                        {formatFileSize(f.size)}
                        {state === 'uploading' && ' · encrypting'}
                        {state === 'done' && ' · uploaded'}
                        {state === 'failed' && ' · did not go through'}
                      </p>
                    </div>
                    {state === 'uploading' ? (
                      <Loader2 className="w-4 h-4 animate-spin text-ink-55" aria-label="Encrypting" />
                    ) : state === 'done' ? (
                      <Check className="w-4 h-4 text-brand" aria-label="Uploaded" />
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(f)}
                        disabled={uploading}
                        aria-label={`Remove ${f.name}`}
                        data-testid="upload-clear-file-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {organisations.length === 0 && (
            <p className="px-3 py-2 rounded-md bg-amber-wash text-amber-ink text-sm">
              Add an organisation before uploading a document.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={uploading}
              data-testid="upload-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={uploading || !files.length || !orgId || !docType}
              data-testid="upload-submit-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {files.length > 1 && uploadingIndex >= 0
                    ? `Encrypting ${uploadingIndex + 1} of ${files.length}`
                    : 'Encrypting'}
                </>
              ) : (
                files.length > 1 ? `Upload and encrypt ${files.length} files` : 'Upload and encrypt'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDocumentDialog;
